import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Grid2, Paper, TextField, Button, FormControlLabel, Checkbox,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Typography, MenuItem, Alert, Snackbar, Toolbar
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ModernNavbar from '../setup/ModernNavbar';
import HeadingTest from '../../BuiltInComponent/HeadingTest';
import {
    applyPaidLeave,
    getEmpPaidLeaves,
    getEmployeeLeaveBalance,
    deletePaidLeave,
    clearError,
    clearSuccess
} from '../../features/PayrollManagement/empPaidLeavesSlice';
import { getEmployeeList } from '../../features/PayrollManagement/employeeSlice';
import { getLeaves } from '../../features/AdminManagement/leaveSlice';
import { checkPeriodLock } from '../../features/PayrollManagement/salarySlice';

const EmployeeLeaveApplication = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { employeeList } = useSelector((state) => state.emp);
    const { leaves } = useSelector((state) => state.leaves);
    const { paidLeaves, leaveBalance, loading, error, success } = useSelector((state) => state.empPaidLeaves);

    const [formData, setFormData] = useState({
        empID: user?.empID || '',
        leaveID: '',
        leaveFromDate: null,
        leaveToDate: null,
        totalDays: 0,
        leaveReason: '',
        adjustFromAllowedLeaves: false
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [selectedLeaveBalance, setSelectedLeaveBalance] = useState(null);
    const [periodLocked, setPeriodLocked] = useState(false);
    const [lockMessage, setLockMessage] = useState('');
    const [selectedEmpUnitID, setSelectedEmpUnitID] = useState(1);

    useEffect(() => {
        if (formData.empID && employeeList && employeeList.length > 0) {
            const selectedEmp = employeeList.find(emp => emp.empID === formData.empID);
            if (selectedEmp?.unitID) {
                setSelectedEmpUnitID(selectedEmp.unitID);
            }
        }
    }, [formData.empID, employeeList]);

    // Check period lock when leave dates change
    useEffect(() => {
        if (formData.leaveFromDate && selectedEmpUnitID) {
            const leaveDate = dayjs(formData.leaveFromDate).toDate();
            const month = leaveDate.getMonth() + 1;
            const year = leaveDate.getFullYear();

            dispatch(checkPeriodLock({
                unitID: selectedEmpUnitID,
                selectedYear: year,
                selectedMonth: month
            })).then((res) => {
                if (res.payload?.isLocked) {
                    setPeriodLocked(true);
                    setLockMessage(res.payload?.message || 'Period is locked');
                } else {
                    setPeriodLocked(false);
                    setLockMessage('');
                }
            });
        }
    }, [formData.leaveFromDate, selectedEmpUnitID, dispatch]);

    useEffect(() => {
        if (formData.empID) {
            dispatch(getEmpPaidLeaves(formData.empID));
            dispatch(getEmployeeLeaveBalance(formData.empID));
        }
    }, [formData.empID, dispatch, success]);

    useEffect(() => {
        if (formData.leaveFromDate && formData.leaveToDate) {
            const days = dayjs(formData.leaveToDate).diff(dayjs(formData.leaveFromDate), 'day') + 1;
            setFormData(prev => ({ ...prev, totalDays: days > 0 ? days : 0 }));
        }
    }, [formData.leaveFromDate, formData.leaveToDate]);

    useEffect(() => {
        if (formData.leaveID && leaveBalance.length > 0) {
            const balance = leaveBalance.find(lb => lb.leaveID === formData.leaveID);
            setSelectedLeaveBalance(balance);
        } else {
            setSelectedLeaveBalance(null);
        }
    }, [formData.leaveID, leaveBalance]);

    useEffect(() => {
        if (success) {
            setSnackbar({ open: true, message: 'Leave application submitted successfully!', severity: 'success' });
            dispatch(clearSuccess());
            handleReset();
        }
    }, [success, dispatch]);

    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: error, severity: 'error' });
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'adjustFromAllowedLeaves' ? checked : value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.empID || !formData.leaveFromDate || !formData.leaveToDate || !formData.leaveReason) {
            setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
            return;
        }

        if (formData.totalDays <= 0) {
            setSnackbar({ open: true, message: 'Invalid date range', severity: 'error' });
            return;
        }

        if (periodLocked) {
            setSnackbar({ open: true, message: `Cannot apply leave - ${lockMessage}`, severity: 'error' });
            return;
        }

        if (formData.adjustFromAllowedLeaves && formData.leaveID) {
            if (!selectedLeaveBalance || selectedLeaveBalance.remainingLeaves < formData.totalDays) {
                setSnackbar({
                    open: true,
                    message: `Insufficient leave balance. Available: ${selectedLeaveBalance?.remainingLeaves || 0} days`,
                    severity: 'error'
                });
                return;
            }
        }

        const submitData = {
            ...formData,
            leaveFromDate: dayjs(formData.leaveFromDate).format('YYYY-MM-DD'),
            leaveToDate: dayjs(formData.leaveToDate).format('YYYY-MM-DD'),
            leaveID: formData.leaveID || null
        };

        dispatch(applyPaidLeave(submitData));
    };

    const handleReset = () => {
        setFormData({
            empID: user?.empID || '',
            leaveID: '',
            leaveFromDate: null,
            leaveToDate: null,
            totalDays: 0,
            leaveReason: '',
            adjustFromAllowedLeaves: false
        });
        setSelectedLeaveBalance(null);
    };

    const handleDelete = (paidLeaveID) => {
        if (window.confirm('Are you sure you want to delete this leave application?')) {
            dispatch(deletePaidLeave(paidLeaveID));
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'success';
            case 'Rejected': return 'error';
            case 'Pending': return 'warning';
            default: return 'default';
        }
    };

    return (
        <>
            <ModernNavbar />
            <Toolbar />
            <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
                <HeadingTest title="Employee Leave Application" />

                {/* Application Form */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, color: '#1E90FF', fontWeight: 600 }}>
                        Apply for Paid Leave
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid2 container spacing={3}>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label="Employee"
                                    name="empID"
                                    value={formData.empID}
                                    onChange={handleChange}
                                    // disabled={user?.empID}
                                    SelectProps={{
                                        MenuProps: {
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                    width: 300
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {employeeList && employeeList.map(emp => (
                                        <MenuItem key={emp.empID} value={emp.empID}>
                                            {emp.empName} - {emp.empCNIC}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Leave Type (Optional)"
                                    name="leaveID"
                                    value={formData.leaveID}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {leaves && leaves.map(leave => (
                                        <MenuItem key={leave.leaveID} value={leave.leaveID}>
                                            {leave.leaveDescription}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {selectedLeaveBalance && (
                                    <Typography variant="caption" sx={{ color: '#1E90FF', mt: 0.5, display: 'block' }}>
                                        Available: {selectedLeaveBalance.remainingLeaves} / {selectedLeaveBalance.allowedDays} days
                                    </Typography>
                                )}
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="From Date"
                                        value={formData.leaveFromDate}
                                        onChange={(date) => setFormData(prev => ({ ...prev, leaveFromDate: date }))}
                                        slotProps={{ textField: { fullWidth: true, required: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="To Date"
                                        value={formData.leaveToDate}
                                        onChange={(date) => setFormData(prev => ({ ...prev, leaveToDate: date }))}
                                        minDate={formData.leaveFromDate}
                                        slotProps={{ textField: { fullWidth: true, required: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Total Days"
                                    value={formData.totalDays}
                                    disabled
                                    InputProps={{
                                        sx: { bgcolor: '#f0f0f0', fontWeight: 600, color: '#1E90FF' }
                                    }}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    required
                                    multiline
                                    rows={3}
                                    label="Leave Reason"
                                    name="leaveReason"
                                    value={formData.leaveReason}
                                    onChange={handleChange}
                                    placeholder="Please provide a detailed reason for your leave request..."
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="adjustFromAllowedLeaves"
                                            checked={formData.adjustFromAllowedLeaves}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Adjust from Allowed Leaves (if unchecked, leave will be approved based on valid reason)"
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12 }}>
                                {periodLocked && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        <strong>Period Locked:</strong> {lockMessage}. You cannot apply for leave during this period.
                                    </Alert>
                                )}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={loading || periodLocked}
                                        sx={{
                                            bgcolor: '#1E90FF',
                                            '&:hover': { bgcolor: '#1c86ee' },
                                            px: 4
                                        }}
                                    >
                                        {loading ? 'Submitting...' : 'Submit Application'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        onClick={handleReset}
                                        sx={{ borderColor: '#1E90FF', color: '#1E90FF' }}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                            </Grid2>
                        </Grid2>
                    </form>
                </Paper>

                {/* Leave History Table */}
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#1E90FF', fontWeight: 600 }}>
                        My Leave Applications
                    </Typography>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#1E90FF' }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Leave Type</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>From Date</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>To Date</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Days</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reason</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Adjusted</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Remarks</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paidLeaves.length > 0 ? (
                                    paidLeaves.map((leave) => (
                                        <TableRow key={leave.paidLeaveID} hover>
                                            <TableCell>{leave.leaveName || 'N/A'}</TableCell>
                                            <TableCell>{dayjs(leave.leaveFromDate).format('DD-MMM-YYYY')}</TableCell>
                                            <TableCell>{dayjs(leave.leaveToDate).format('DD-MMM-YYYY')}</TableCell>
                                            <TableCell>
                                                <Chip label={leave.totalDays} color="primary" size="small" />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {leave.leaveReason}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={leave.adjustFromAllowedLeaves ? 'Yes' : 'No'}
                                                    color={leave.adjustFromAllowedLeaves ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={leave.approvalStatus}
                                                    color={getStatusColor(leave.approvalStatus)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {leave.approvalRemarks || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {leave.approvalStatus === 'Pending' && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(leave.paidLeaveID)}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center">
                                            <Typography color="textSecondary">No leave applications found</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};

export default EmployeeLeaveApplication;
